const API_ROOT = '/studentplayground/api/admin/calendar';
const PALETTE = ['#1a73e8', '#d93025', '#188038', '#a142f4', '#e37400', '#007b83', '#c5221f', '#5f6368'];
const VILNIUS_DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Vilnius', year: 'numeric', month: '2-digit', day: '2-digit',
});
const VILNIUS_TIME_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Vilnius', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

function dateKey(date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

export function calendarDateKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = VILNIUS_DATE_FORMAT.formatToParts(date);
  const fields = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${fields.year}-${fields.month}-${fields.day}`;
}

export function calendarTime(value) {
  const parts = VILNIUS_TIME_FORMAT.formatToParts(new Date(value));
  const fields = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${fields.hour}:${fields.minute}`;
}

function dayRecord(date, focusMonth) {
  return { date, key: dateKey(date), inMonth: focusMonth == null || date.getUTCMonth() === focusMonth };
}

export function weekdayLabel(value, style = 'long') {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return '';
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (date.getUTCFullYear() !== Number(year)
    || date.getUTCMonth() !== Number(month) - 1
    || date.getUTCDate() !== Number(day)) return '';
  return new Intl.DateTimeFormat('en-GB', {
    weekday: style === 'short' ? 'short' : 'long',
    timeZone: 'UTC',
  }).format(date);
}

export function weekDays(value) {
  const date = new Date(value);
  const mondayOffset = (date.getUTCDay() + 6) % 7;
  const monday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - mondayOffset));
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(monday);
    day.setUTCDate(day.getUTCDate() + index);
    return dayRecord(day);
  });
}

export function monthGrid(value) {
  const focus = new Date(value);
  const year = focus.getUTCFullYear();
  const month = focus.getUTCMonth();
  const offset = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const slots = Math.ceil((offset + daysInMonth) / 7) * 7;
  return Array.from({ length: slots }, (_, index) => {
    const dayNumber = index - offset + 1;
    return dayNumber < 1 || dayNumber > daysInMonth
      ? { date: null, key: null, inMonth: false }
      : dayRecord(new Date(Date.UTC(year, month, dayNumber)), month);
  });
}

export function viewDateRange(value, view) {
  const focus = new Date(value);
  if (view === 'week') {
    const days = weekDays(focus);
    const last = new Date(days[6].date);
    last.setUTCDate(last.getUTCDate() + 1);
    return { dateStart: days[0].key, dateEnd: dateKey(last) };
  }
  const year = focus.getUTCFullYear();
  const month = focus.getUTCMonth();
  return {
    dateStart: dateKey(new Date(Date.UTC(year, month, 1))),
    dateEnd: dateKey(new Date(Date.UTC(year, month + 1, 1))),
  };
}

export function classTotal(events, visibleDays) {
  const visibleKeys = new Set(visibleDays.filter((day) => day.key).map((day) => day.key));
  return new Set((events || [])
    .filter((event) => visibleKeys.has(calendarDateKey(event.start)))
    .map((event) => `${event.calendar_id}:${event.id}`)).size;
}

export function stableCalendarColor(value) {
  let hash = 2166136261;
  for (const character of String(value || '')) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

export function parseRecurrence(rules = []) {
  const rule = rules.find((item) => item.startsWith('RRULE:')) || '';
  const values = Object.fromEntries(rule.slice(6).split(';').filter(Boolean).map((part) => {
    const [key, value] = part.split('=');
    return [key, value];
  }));
  return {
    frequency: (values.FREQ || 'WEEKLY').toLowerCase(),
    interval: Number(values.INTERVAL || 1),
    weekdays: values.BYDAY ? values.BYDAY.split(',') : [],
  };
}

function actionTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    hourCycle: 'h23', timeZone: 'Europe/Vilnius',
  }).format(date);
}

function actionTimeRange(snapshot) {
  const start = actionTime(snapshot.start);
  const end = actionTime(snapshot.end);
  if (!start) return '';
  if (!end) return start;
  const day = start.split(', ')[0];
  return end.startsWith(`${day}, `) ? `${start}–${end.slice(day.length + 2)}` : `${start}–${end}`;
}

function actionRecurrence(value) {
  if (!value || (Array.isArray(value) && !value.length) || value.enabled === false) return '';
  const parsed = Array.isArray(value) ? parseRecurrence(value) : value;
  if (!parsed.frequency) return '';
  const frequency = parsed.frequency === 'daily' ? 'day' : 'week';
  const interval = Number(parsed.interval || 1);
  const weekday = (parsed.weekdays || []).join(', ');
  return `Every ${interval > 1 ? `${interval} ${frequency}s` : frequency}${weekday ? ` · ${weekday}` : ''}`;
}

function actionPrice(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `€${number.toFixed(2)}` : '';
}

export function actionDetailRows(details) {
  const before = details.before || {};
  const after = details.after || {};
  const update = details.operation === 'update';
  const current = details.operation === 'cancel' ? before : after;
  const rows = [];
  const add = (label, oldValue, newValue) => {
    if (update) {
      if (oldValue !== newValue) rows.push({ label, before: oldValue || '—', after: newValue || '—' });
    } else if (newValue) rows.push({ label, before: '', after: newValue });
  };
  add('Class', update ? before.title || '' : '', (update ? after : current).title || '');
  add('Time', update ? actionTimeRange(before) : '', actionTimeRange(update ? after : current));
  add('Students', update ? before.description || '' : '', (update ? after : current).description || '');
  if (details.scope !== 'single') {
    add('Repeats', update ? actionRecurrence(before.recurrence) : '', actionRecurrence((update ? after : current).recurrence));
  }
  const oldPrices = new Map((before.student_prices || []).map((item) => [String(item.student_id), item]));
  const newPrices = new Map((after.student_prices || []).map((item) => [String(item.student_id), item]));
  const ids = update ? new Set([...oldPrices.keys(), ...newPrices.keys()]) : new Set((current.student_prices || []).map((item) => String(item.student_id)));
  for (const id of ids) {
    const oldItem = oldPrices.get(id);
    const newItem = newPrices.get(id);
    const currentItem = details.operation === 'cancel' ? oldItem : newItem;
    const name = (newItem || oldItem)?.student_name || `Student #${id}`;
    add(`Price — ${name}`, update ? actionPrice(oldItem?.base_price) : '', actionPrice(currentItem?.base_price));
  }
  return rows;
}

export function buildMutationPayload(form) {
  const suffix = ' | Tutory';
  let title = String(form.title || '').trim();
  while (title.endsWith(suffix)) title = title.slice(0, -suffix.length).trim();
  const selected = new Set((form.selectedStudentIds || []).map(Number));
  const students = (form.students || []).filter((student) => selected.has(Number(student.id)));
  return {
    ...form,
    title,
    description: String(form.description || '').trim() || students.map((student) => student.token).join(' / '),
    student_prices: students.map((student) => ({ student_id: student.id, base_price: student.basePrice })),
  };
}

export function filterStudents(students, query = '') {
  const needle = String(query).trim().toLocaleLowerCase();
  if (!needle) return [...(students || [])];
  return (students || []).filter((student) => [student.name, student.token, student.grade]
    .some((value) => String(value || '').toLocaleLowerCase().includes(needle)));
}

export function generatedClassTitle(subject, students, groupLetter = 'A') {
  if (!subject || !students?.length) return '';
  const base = students.length === 1
    ? `${students[0].title_prefix} ${subject.calendar_label} Pamoka`
    : `${subject.calendar_label} Grupė ${groupLetter || 'A'}`;
  return `${base} | Tutory`;
}

export function oneHourAfter(startTime) {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(startTime)) return null;
  const [hours, minutes] = startTime.split(':').map(Number);
  return `${String((hours + 1) % 24).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function defaultClassTimes(now = new Date()) {
  const rounded = new Date(Math.ceil(now.getTime() / 300000) * 300000);
  const startTime = calendarTime(rounded);
  return { startDate: calendarDateKey(rounded), startTime, endTime: oneHourAfter(startTime) };
}

export function buildTimedRange(date, startTime, endTime) {
  let endDate = date;
  if (oneHourAfter(startTime) && oneHourAfter(endTime) && endTime < startTime && date) {
    const nextDay = new Date(`${date}T12:00:00Z`);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    endDate = nextDay.toISOString().slice(0, 10);
  }
  return {
    start: `${date}T${startTime}:00`,
    end: `${endDate}T${endTime}:00`,
  };
}

export function unavailableTimeRanges(events, calendarId, selectedDate, excludedEventId = '') {
  if (!calendarId || !selectedDate) return [];
  const dayStart = new Date(`${selectedDate}T00:00:00`);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);
  const clock = (value) => `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;

  return (events || [])
    .filter((event) => event.calendar_id === calendarId && event.id !== excludedEventId)
    .map((event) => ({ event, start: new Date(event.start), end: new Date(event.end) }))
    .filter(({ start, end }) => !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start < dayEnd && end > dayStart)
    .sort((left, right) => left.start - right.start)
    .map(({ event, start, end }) => ({
      eventId: event.id,
      label: event.all_day ? 'All day' : `${clock(start < dayStart ? dayStart : start)}–${clock(end > dayEnd ? dayEnd : end)}`,
    }));
}

export function mergeCalendarHealth(calendars, refreshed) {
  const byId = new Map((refreshed || []).map((calendar) => [calendar.id, calendar]));
  return calendars.map((calendar) => {
    if (!byId.has(calendar.id)) return calendar;
    const health = { ...byId.get(calendar.id) };
    delete health.hidden;
    return { ...calendar, ...health };
  });
}

export function calendarsNeedingValidation(calendars) {
  return (calendars || [])
    .filter((calendar) => calendar.id && calendar.needs_validation)
    .map((calendar) => calendar.id);
}

export function filterAndSortCalendars(calendars, query = '') {
  const needle = String(query).trim().toLocaleLowerCase();
  const priority = (status) => {
    if (status === 'ready' || status === 'no_classes') return 0;
    if (status === 'checking') return 1;
    if (status === 'invalid') return 3;
    return 2;
  };
  return calendars
    .filter((calendar) => !needle || [calendar.name, calendar.teacher_name, calendar.id]
      .some((value) => String(value || '').toLocaleLowerCase().includes(needle)))
    .slice()
    .sort((left, right) => priority(left.status) - priority(right.status)
      || String(left.name).localeCompare(String(right.name), undefined, { sensitivity: 'base' }));
}

async function request(path, init = {}) {
  const token = globalThis.localStorage?.getItem('jwtToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init.headers || {}),
  };
  const response = await fetch(`${API_ROOT}${path}`, {
    ...init,
    headers,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.detail || data.code || `Calendar request failed (${response.status})`);
    error.code = data.code || '';
    error.data = data;
    throw error;
  }
  return data;
}

export const calendarApi = {
  options: () => request('/options/'),
  setTeacherVisibility: (teacherId, hidden) => request(`/teachers/${encodeURIComponent(teacherId)}/visibility/`, {
    method: 'PATCH', body: JSON.stringify({ hidden }),
  }),
  health: (calendarIds) => request('/health/', { method: 'POST', body: JSON.stringify({ calendar_ids: calendarIds }) }),
  events: (timeMin, timeMax, calendarIds) => request('/events/', {
    method: 'POST',
    body: JSON.stringify({ time_min: timeMin, time_max: timeMax, calendar_ids: calendarIds }),
  }),
  eventsInView: (dateStart, dateEnd, selection) => request('/events/', {
    method: 'POST',
    body: JSON.stringify({
      date_start: dateStart,
      date_end: dateEnd,
      ...(selection.kind === 'student'
        ? { student_id: selection.id }
        : { calendar_ids: selection.ids }),
    }),
  }),
  actions: () => request('/actions/'),
  action: (mutationId) => request(`/mutations/${encodeURIComponent(mutationId)}/`),
  create: (payload) => request('/classes/', { method: 'POST', body: JSON.stringify(payload) }),
  update: (eventId, payload) => request(`/classes/${encodeURIComponent(eventId)}/`, { method: 'PATCH', body: JSON.stringify(payload) }),
  cancel: (eventId, payload) => request(`/classes/${encodeURIComponent(eventId)}/`, { method: 'DELETE', body: JSON.stringify(payload) }),
  retry: (mutationId) => request(`/actions/${encodeURIComponent(mutationId)}/retry/`, { method: 'POST', body: '{}' }),
  reconcile: (mutationId) => request(`/mutations/${encodeURIComponent(mutationId)}/reconcile/`, { method: 'POST', body: '{}' }),
};
